// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The event log.
 *
 * It exists so "your code never leaves the tab" is something you can watch
 * rather than something you are asked to believe. Every registry request is
 * written here as it happens, by name, and there is no line for an upload
 * because there is no upload — the log and the browser's own network panel
 * should agree exactly, which is the point.
 *
 * NOTHING FROM THE PASTED DOCUMENT IS EVER LOGGED. Package names, counts and
 * URLs only. Never an evidence line, never a fragment of source. The drawer is
 * exactly what somebody screenshots when reporting a problem.
 */

export type LogKind = 'info' | 'good' | 'warn' | 'bad' | 'net';

export interface LogEntry {
  at: Date;
  kind: LogKind;
  message: string;
}

const MAX_ENTRIES = 600;

export class EventLog {
  private entries: LogEntry[] = [];
  private listeners = new Set<(entries: readonly LogEntry[]) => void>();

  add(message: string, kind: LogKind = 'info'): void {
    this.entries.push({ at: new Date(), kind, message });
    if (this.entries.length > MAX_ENTRIES) this.entries.splice(0, this.entries.length - MAX_ENTRIES);
    this.listeners.forEach((fn) => fn(this.entries));
  }

  subscribe(fn: (entries: readonly LogEntry[]) => void): () => void {
    this.listeners.add(fn);
    fn(this.entries);
    return () => this.listeners.delete(fn);
  }

  all(): readonly LogEntry[] {
    return this.entries;
  }

  toText(): string {
    return this.entries.map((e) => `[${stamp(e.at)}] ${e.message}`).join('\n');
  }
}

export function stamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
